import {useCallback, useEffect} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import getPartnersQuery from '../graphql/getPartnersQuery';
import {
  partnersFetchFailed,
  partnersFetchStarted,
  partnersFetchSucceeded
} from '../store/slice';
import {Partner} from '../store/types';

interface UseBusPartnersResult {
  busPartners: Partner[] | null;
  refetchBusPartners: (filters?: FetchBusPartnersFilters) => Promise<void>;
}

interface FetchBusPartnersFilters {
  countryIds?: number[];
}

export const useBusPartners = (): UseBusPartnersResult => {
  const busPartners = useAppSelector(({partners}) => partners.list);
  const busPartnersLoading = useAppSelector(
    ({partners}) => partners.partnersLoading
  );
  const dispatch = useAppDispatch();

  const [fetchBusPartners] = useLazyQuery<{busPartners: Partner[]}>(
    getPartnersQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache'
    }
  );

  const handleError = useCallback(
    (errorMessage: string) => {
      console.error(errorMessage);
      dispatch(partnersFetchFailed());
    },
    [dispatch]
  );

  const getBusPartners = useCallback(
    async (filters: FetchBusPartnersFilters, force = false) => {
      // Only fetch if bus partners are missing AND not already loading, unless forced
      if (!force && (Array.isArray(busPartners) || busPartnersLoading)) return;

      try {
        dispatch(partnersFetchStarted());
        const {data, error} = await fetchBusPartners({
          variables: filters
        });

        if (error) {
          const errorMessage = error.message ?? 'Failed to fetch bus partners.';
          handleError(errorMessage);
          return;
        }

        const fetchedBusPartners = data?.busPartners ?? [];
        dispatch(partnersFetchSucceeded(fetchedBusPartners));
      } catch (err) {
        handleError(`Unexpected error while fetching bus partners: ${err}`);
      }
    },
    [busPartners, busPartnersLoading, dispatch, fetchBusPartners, handleError]
  );

  useEffect(() => {
    getBusPartners({});
  }, []); // eslint-disable-line

  const refetchBusPartners = useCallback(
    (filters: FetchBusPartnersFilters = {}) => getBusPartners(filters, true),
    [getBusPartners]
  );

  return {busPartners, refetchBusPartners};
};
